/**
 * Utility functions for handling audio format detection and conversion
 */

/**
 * Get file extension from MIME type
 * @param mimeType - The MIME type of the audio file
 * @returns The appropriate file extension
 */
export function getAudioFileExtension(mimeType: string | undefined): string {
  if (!mimeType) {
    return 'audio'; // Generic fallback
  }

  // Normalize MIME type to lowercase
  const normalizedType = mimeType.toLowerCase();

  // Map common audio/video MIME types to file extensions
  const mimeToExtension: Record<string, string> = {
    // Audio formats
    'audio/mpeg': 'mp3',
    'audio/mp3': 'mp3',
    'audio/wav': 'wav',
    'audio/wave': 'wav',
    'audio/x-wav': 'wav',
    'audio/ogg': 'ogg',
    'audio/webm': 'webm',
    'audio/aac': 'aac',
    'audio/mp4': 'm4a',
    'audio/x-m4a': 'm4a',
    'audio/flac': 'flac',
    'audio/x-flac': 'flac',
    // Video formats (for files that may contain audio tracks)
    'video/mp4': 'mp4',
    'video/mpeg': 'mpeg',
    'video/ogg': 'ogv',
    'video/webm': 'webm',
    'video/x-flv': 'flv',
    'video/x-m4v': 'm4v',
    'video/quicktime': 'mov',
    'video/3gpp': '3gp',
    'video/3gpp2': '3g2',
  };

  // Check if we have a mapping for this MIME type (strip parameters like "; codecs=...")
  const baseMimeType = normalizedType.split(';')[0].trim();
  if (mimeToExtension[baseMimeType]) {
    return mimeToExtension[baseMimeType];
  }

  // Try to extract extension from MIME type (e.g., "audio/mp3" -> "mp3")
  const parts = normalizedType.split('/');
  if (parts.length === 2 && (parts[0] === 'audio' || parts[0] === 'video')) {
    // Remove 'x-' prefix if present
    const format = parts[1].replace(/^x-/, '');
    // Return the format if it looks like a valid extension (3-4 characters)
    if (format.length >= 3 && format.length <= 4 && /^[a-z0-9]+$/.test(format)) {
      return format;
    }
  }

  // Default to 'media' for unknown types
  return 'mp3';
}

/**
 * Generate a filename for audio download
 * @param caseCode - Case code (e.g., "AEPQC-7228")
 * @param caseId - Case ID as fallback if code is not available
 * @param callId - Call ID
 * @param mimeType - MIME type of the audio
 * @returns Generated filename with appropriate extension (e.g., "AEPQC-7228_call123.mp3")
 */
export function generateAudioFilename(
  caseCode: string | undefined,
  caseId: string,
  callId: string,
  mimeType?: string,
): string {
  const extension = getAudioFileExtension(mimeType);
  const identifier = caseCode || caseId;
  return `${identifier}_${callId}.${extension}`;
}
